export type OnboardingSessionData = {
  selectedRecipient: string | null;
  otherRecipientText: string;
  recipientName: string;
  selectedOccasion: string | null;
  otherOccasionText: string;
  recipientSpecialDescription: string;
  favoriteMemoriesDescription: string;
  heartMessageDescription: string;
  selectedGenres: string[];
  selectedStyles: string[];
  selectedTempo: string | null;
  selectedInstruments: string[];
};

const ONBOARDING_SESSION_KEY = "musicmaker_onboarding";
/** Legacy key — cleared on load so parallel tabs do not inherit prefilled onboarding. */
const ONBOARDING_SESSION_BACKUP_KEY = "musicmaker_onboarding_backup";
const LAST_ORDER_REF_KEY = "musicmaker_last_order";
/** Set before signup redirect; cleared after track-song loads. Session-only so new tabs are unaffected. */
const AWAITING_TRACK_SONG_KEY = "musicmaker_await_track_song";

export function markAwaitingTrackSong(): void {
  sessionStorage.setItem(AWAITING_TRACK_SONG_KEY, "1");
}

export function isAwaitingTrackSong(): boolean {
  return sessionStorage.getItem(AWAITING_TRACK_SONG_KEY) === "1";
}

export function clearAwaitingTrackSong(): void {
  sessionStorage.removeItem(AWAITING_TRACK_SONG_KEY);
}

export type LastOrderRef = {
  docId: string;
  userId: string;
  locale?: string;
};

export function saveLastOrderRef(ref: LastOrderRef): void {
  localStorage.setItem(LAST_ORDER_REF_KEY, JSON.stringify(ref));
}

export function loadLastOrderRef(): LastOrderRef | null {
  const raw = localStorage.getItem(LAST_ORDER_REF_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as LastOrderRef;
  } catch {
    return null;
  }
}

export function clearLastOrderRef(): void {
  localStorage.removeItem(LAST_ORDER_REF_KEY);
}

export function saveOnboardingSession(data: OnboardingSessionData): void {
  const json = JSON.stringify(data);
  sessionStorage.setItem(ONBOARDING_SESSION_KEY, json);
}

export function loadOnboardingSession(): OnboardingSessionData | null {
  localStorage.removeItem(ONBOARDING_SESSION_BACKUP_KEY);
  const raw = sessionStorage.getItem(ONBOARDING_SESSION_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as OnboardingSessionData;
  } catch {
    return null;
  }
}

export function clearOnboardingSession(): void {
  sessionStorage.removeItem(ONBOARDING_SESSION_KEY);
  localStorage.removeItem(ONBOARDING_SESSION_BACKUP_KEY);
}

/** True when the document was loaded via browser refresh (F5 / reload). */
export function isOnboardingPageReload(): boolean {
  if (typeof performance === "undefined") return false;
  const [entry] = performance.getEntriesByType(
    "navigation",
  ) as PerformanceNavigationTiming[];
  return entry?.type === "reload";
}
